/**
 * Bring a stored key-binding config up to the current defaults (modular action
 * bars plan, phase 3).
 *
 * 1. Old slot action names become the bar scheme (`ability_slot_1` → `bar1_slot1`,
 *    `ability_N` → `bar1_slot(N+1)`), keeping each key and modifier.
 * 2. Stored copies of retired defaults are dropped (exact action + key only).
 * 3. Defaults the stored copy lacks are added, but only ones the player has never
 *    had. A config remembers which defaults it has already been given
 *    (`defaultsSeen`), so a key the player rebound or cleared stays that way
 *    through later version bumps. Before v3.0 there was no such list; for those
 *    saves a default is added only for an action with no binding at all, plus the
 *    defaults new in v3.0. Before this, every bump gave a rebound slot its old key
 *    back as a second key.
 *
 * A default is never added onto a key another action already holds, unless the
 * defaults themselves share that key between the two actions (Interact and Loot on E).
 */
import type { KeyBinding, KeyBindingsConfig } from '../../shared/CharacterTypes';
import type { StoredKeyBindings } from '../../shared/types/Input';
import { legacySlotAction } from '../../shared/hotbar/slotActions';

export type { StoredKeyBindings };

export const bindingKeyId = (b: Pick<KeyBinding, 'action' | 'key' | 'modifier'>): string =>
  `${b.action}::${b.key.toUpperCase()}::${b.modifier ?? ''}`;

const sameKey = (a: Pick<KeyBinding, 'key' | 'modifier'>, b: Pick<KeyBinding, 'key' | 'modifier'>) =>
  a.key.toUpperCase() === b.key.toUpperCase() && (a.modifier ?? '') === (b.modifier ?? '');

/** Defaults added in v3.0 that a pre-v3.0 save gets even though their action is bound: numpad 7–0. */
const NEW_IN_3_0 = new Set(['bar1_slot7::NUMPAD_SEVEN::', 'bar1_slot8::NUMPAD_EIGHT::', 'bar1_slot9::NUMPAD_NINE::', 'bar1_slot10::NUMPAD_ZERO::']);

export function migrateKeyBindings(
  stored: StoredKeyBindings,
  defaults: KeyBindingsConfig,
  retired: ReadonlyArray<{ action: string; key: string }>,
): StoredKeyBindings {
  const renamed = stored.bindings.map((b) => {
    const action = legacySlotAction(b.action);
    return action ? { ...b, action } : b;
  });
  const merged: KeyBinding[] = [];
  for (const binding of renamed) {
    const isRetired = retired.some((r) => r.action === binding.action && r.key.toUpperCase() === binding.key.toUpperCase());
    if (!isRetired && !merged.some((b) => bindingKeyId(b) === bindingKeyId(binding))) merged.push(binding);
  }

  const seen = stored.defaultsSeen ? new Set(stored.defaultsSeen) : null;
  const boundBefore = new Set(merged.map((b) => b.action));
  for (const def of defaults.bindings) {
    const id = bindingKeyId(def);
    if (merged.some((b) => bindingKeyId(b) === id)) continue;
    const owed = seen ? !seen.has(id) : !boundBefore.has(def.action) || NEW_IN_3_0.has(id);
    if (!owed) continue;
    const taken = merged.some((b) => b.action !== def.action && sameKey(b, def)
      && !defaults.bindings.some((d) => d.action === b.action && sameKey(d, def)));
    if (!taken) merged.push({ ...def });
  }

  return {
    version: defaults.version,
    bindings: merged,
    defaultsSeen: defaults.bindings.map(bindingKeyId),
  };
}
